/**
 * A half-written request, kept so that leaving the form does not throw it away.
 *
 * The creation form is four steps and its description field takes up to 4,000 characters
 * (D-44). None of that survived a stray click on the sidebar, the browser's back button or a
 * reload: the whole thing was component state, so somebody who had spent five minutes
 * describing a problem lost it to one mis-click and had to start again — which is the point
 * at which people stop raising tickets and ring the desk instead.
 *
 * ⚠️ sessionStorage, not localStorage, and the difference is the point — the same reasoning
 * as `ticket-tabs`, for the same reason and on the same machines.
 *
 * A draft is free text somebody typed about their own problem, on a healthcare service desk,
 * at a shared clinical workstation. localStorage outlives the browser session, so the next
 * person to sit down would find the last person's half-written request already in the form.
 * sessionStorage dies with the tab, and `clearRequestDraft` empties it on sign-out so it does
 * not survive even that long.
 *
 * ⚠️ WHAT IS DELIBERATELY NOT KEPT: the chosen files, which cannot be serialised and must be
 * re-picked; and the people invited onto the request, which are directory records about
 * colleagues rather than anything the author typed. A draft holds what somebody would have to
 * type again, and nothing else.
 *
 * ⚠️ It is a convenience and never a record. Nothing here has been submitted, nothing is
 * authorised against it, and every value in it is re-validated server-side on create exactly
 * as though it had just been typed.
 */

const STORAGE_KEY = "tf-request-draft";

/** Same-document writes do not raise `storage`, so nothing here listens for one. */
export interface RequestDraft {
  teamId: string;
  categoryId: string;
  subject: string;
  description: string;
  siteId: string;
  contactPhone: string;
  contactExtension: string;

  /**
   * Who the request is for, when it is not the author.
   *
   * ⚠️ Kept so a restored draft cannot silently change whose request it is. `forUser` also
   * lives in the URL, and a draft saved while raising one for a colleague must not reappear
   * on a form that has since been opened for the author themselves — see `readRequestDraft`,
   * which refuses to return a draft whose subject does not match the form asking for it.
   */
  forUser: string | null;
}

const EMPTY: RequestDraft = {
  teamId: "",
  categoryId: "",
  subject: "",
  description: "",
  siteId: "",
  contactPhone: "",
  contactExtension: "",
  forUser: null,
};

/** Whether anything has been typed that would be a loss. */
export function draftHasContent(draft: RequestDraft): boolean {
  return draft.subject.trim() !== "" || draft.description.trim() !== "";
}

/**
 * The stored draft, if it belongs to the form asking for it.
 *
 * ⚠️ `forUser` has to match, and a mismatch discards rather than adapts. A draft written for
 * a colleague must never reappear on a form the author has since opened for themselves: the
 * text was written about somebody else's problem, and the one thing worse than losing a draft
 * is filing it against the wrong person.
 */
export function readRequestDraft(forUser: string | null): RequestDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;

    // Validated rather than trusted. It is our own data, but it is data from outside the
    // running program, and a malformed entry should be dropped rather than rendered.
    const draft = { ...EMPTY };

    for (const key of Object.keys(EMPTY) as (keyof RequestDraft)[]) {
      const value = (parsed as Record<string, unknown>)[key];

      if (key === "forUser") {
        draft.forUser = typeof value === "string" ? value : null;
      } else if (typeof value === "string") {
        draft[key] = value;
      }
    }

    if ((draft.forUser ?? null) !== (forUser ?? null)) return null;

    return draftHasContent(draft) ? draft : null;
  } catch {
    return null;
  }
}

export function writeRequestDraft(draft: RequestDraft) {
  try {
    // Nothing typed yet is nothing worth keeping — and it means abandoning a form after
    // only choosing a team leaves no trace to explain on the next visit.
    if (!draftHasContent(draft)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage full, or blocked by policy. A draft is a convenience; losing it must never
    // take the form down with it.
  }
}

export function clearRequestDraft() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing worth doing */
  }
}
